import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useDataProvider, useLogin, useNotify, useTranslate } from "ra-core";
import { useForm, type SubmitHandler } from "react-hook-form";
import { Navigate, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { brandLogo, brandMark } from "../root/logos/brand";
import type { CrmDataProvider } from "../providers/types";
import type { SignUpData } from "../types";
import { LoginSkeleton } from "./LoginSkeleton";
import { Notification } from "@/components/admin/notification";
import { ConfirmationRequired } from "./ConfirmationRequired";
import { SSOAuthButton } from "./SSOAuthButton";
import { googleWorkplaceDomain } from "./authConfig";

export const SignupPage = () => {
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const title = "Georgia Web Co CRM";
  const logo = brandLogo;
  const mark = brandMark;
  const { data: isInitialized, isPending } = useQuery({
    queryKey: ["init"],
    queryFn: async () => {
      return dataProvider.isInitialized();
    },
  });

  const { isPending: isSignUpPending, mutate } = useMutation({
    mutationKey: ["signup"],
    mutationFn: async (data: SignUpData) => {
      return dataProvider.signUp(data);
    },
    onSuccess: (data) => {
      login({
        email: data.email,
        password: data.password,
        redirectTo: "/contacts",
      })
        .then(() => {
          notify("crm.auth.signup.initial_user_created", {
            messageArgs: {
              _: "Initial user successfully created",
            },
          });
          queryClient.invalidateQueries({
            queryKey: ["auth", "canAccess"],
          });
        })
        .catch((err) => {
          if (err.code === "email_not_confirmed") {
            navigate(ConfirmationRequired.path);
          } else {
            notify("crm.auth.sign_in_failed", {
              type: "error",
              messageArgs: {
                _: "Failed to log in.",
              },
            });
            navigate("/login");
          }
        });
    },
    onError: (error) => {
      notify(error.message);
    },
  });

  const login = useLogin();
  const notify = useNotify();

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<SignUpData>({
    mode: "onChange",
  });

  if (isPending) {
    return <LoginSkeleton />;
  }

  // For the moment, we only allow one user to sign up. Other users must be created by the administrator.
  if (isInitialized) {
    return <Navigate to="/login" />;
  }

  const onSubmit: SubmitHandler<SignUpData> = async (data) => {
    mutate(data);
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <div className="relative grid w-full lg:grid-cols-2">
        <div className="relative hidden min-h-screen flex-col p-10 text-white lg:flex">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 80% 60% at 20% 10%, rgba(253,118,26,0.35), transparent 55%), radial-gradient(ellipse 50% 40% at 90% 90%, rgba(253,118,26,0.12), transparent 45%), #0f1626",
            }}
          />
          <div className="relative z-20 flex items-center gap-3">
            <img className="h-10 w-auto" src={mark} alt={title} />
            <span className="text-white text-lg font-semibold tracking-tight">
              Georgia Web Co{" "}
              <span className="text-[#fd761a]">CRM</span>
            </span>
          </div>
          <div className="relative z-20 mt-auto max-w-md">
            <p className="text-3xl font-semibold leading-tight tracking-tight text-white">
              Organize customers, jobs, and follow-ups
            </p>
            <p className="mt-4 text-base text-white/80 leading-relaxed">
              Georgia Web Co CRM keeps leads, quotes, and reminders in one place
              — built for local service businesses.
            </p>
          </div>
        </div>

        <div className="flex flex-col justify-center w-full px-6 py-10 sm:px-10 lg:p-12 bg-[#ffffff] text-[#0f1626]">
          <div className="w-full max-w-[420px] mx-auto space-y-8">
            <div className="flex items-center gap-3 lg:hidden mb-1">
              <img
                className="h-12 w-auto max-w-[260px] object-contain object-left"
                src={logo}
                alt={title}
              />
            </div>

            <div className="hidden lg:block mb-2">
              <img
                className="h-12 w-auto max-w-[280px] object-contain object-left"
                src={logo}
                alt={title}
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#b84a00]">
                First-time setup
              </p>
              <h1 className="text-3xl font-bold tracking-tight text-[#0f1626]">
                Create your admin account
              </h1>
              <p className="text-base text-[#3d4654] leading-relaxed">
                {translate("crm.auth.signup.create_first_user", {
                  _: "Create the first user account to complete the setup.",
                })}
              </p>
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4 rounded-xl border-2 border-[#c5ceda] bg-[#ffffff] p-6 shadow-[0_12px_32px_rgba(15,22,38,0.1)]"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="first_name" className="text-[#0f1626] font-medium">
                    {translate("crm.auth.first_name")}
                  </Label>
                  <Input
                    {...register("first_name", { required: true })}
                    id="first_name"
                    type="text"
                    autoComplete="given-name"
                    required
                    className="bg-white border-2 border-[#c5ceda] text-[#0f1626] h-11"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="last_name" className="text-[#0f1626] font-medium">
                    {translate("crm.auth.last_name")}
                  </Label>
                  <Input
                    {...register("last_name", { required: true })}
                    id="last_name"
                    type="text"
                    autoComplete="family-name"
                    required
                    className="bg-white border-2 border-[#c5ceda] text-[#0f1626] h-11"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email" className="text-[#0f1626] font-medium">
                  {translate("ra.auth.email")}
                </Label>
                <Input
                  {...register("email", { required: true })}
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="bg-white border-2 border-[#c5ceda] text-[#0f1626] h-11"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="password" className="text-[#0f1626] font-medium">
                  {translate("ra.auth.password")}
                </Label>
                <Input
                  {...register("password", { required: true, minLength: 6 })}
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="bg-white border-2 border-[#c5ceda] text-[#0f1626] h-11"
                />
              </div>
              <div className="flex flex-col gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={!isValid || isSignUpPending}
                  className="w-full h-12 text-base font-semibold bg-[#fd761a] hover:bg-[#b84a00] text-white"
                >
                  {isSignUpPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {translate("crm.auth.signup.creating", {
                        _: "Creating...",
                      })}
                    </>
                  ) : (
                    translate("crm.auth.signup.create_account", {
                      _: "Create account",
                    })
                  )}
                </Button>
                {googleWorkplaceDomain ? (
                  <SSOAuthButton
                    className="w-full"
                    domain={googleWorkplaceDomain}
                  >
                    {translate("crm.auth.sign_in_google_workspace", {
                      _: "Sign in with Google Workplace",
                    })}
                  </SSOAuthButton>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};

SignupPage.path = "/sign-up";
